import { useCallback, useEffect, useState } from 'react'
import { api, type Erinnerung } from '../api'
import { erinnerungsZeit } from '../erinnerungen'
import { link } from '../navigation'
import { PushHinweis } from './PushHinweis'

const SPAETER = [
  { minuten: 10, text: 'in 10 Minuten' },
  { minuten: 60, text: 'in 1 Stunde' },
  { minuten: 180, text: 'in 3 Stunden' },
  { minuten: 24 * 60, text: 'morgen' },
]

/** Fällige und anstehende Erinnerungen; fällige stehen oben und bleiben, bis du sie abhakst oder verschiebst. */
export function Erinnerungen({ onGeaendert }: { onGeaendert: () => void }) {
  const [liste, setListe] = useState<Erinnerung[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(
    () =>
      api
        .erinnerungen()
        .then((l) => {
          setListe(l)
          setFehler(null)
        })
        .catch((e) => setFehler((e as Error).message)),
    [],
  )

  useEffect(() => {
    laden()
    // Eine Erinnerung wird fällig, während die Seite offen ist
    const t = setInterval(laden, 20000)
    return () => clearInterval(t)
  }, [laden])

  const aktion = async (f: () => Promise<unknown>) => {
    try {
      await f()
      await laden()
      onGeaendert()
    } catch (e) {
      setFehler((e as Error).message)
    }
  }

  if (!liste || liste.length === 0) return fehler ? <p className="fehler">{fehler}</p> : null
  const faellig = liste.filter((e) => e.status === 'GESENDET')
  const offen = liste.filter((e) => e.status === 'OFFEN')

  const karte = (e: Erinnerung) => (
    <article key={e.id} className={`karte erinnerung${e.status === 'GESENDET' ? ' faellig' : ''}`}>
      <p>
        <strong>⏰ {e.text}</strong>
      </p>
      <p className="leise klein">
        {erinnerungsZeit(e)}
        {e.notizId && (
          <>
            {' · '}
            <a href={link.notiz(e.notizId)}>aus deiner Notiz</a>
          </>
        )}
      </p>
      <footer>
        {e.status === 'GESENDET' && <button onClick={() => aktion(() => api.erinnerungErledigt(e.id))}>Erledigt</button>}
        <label className="spaeter">
          <span className="leise klein">{e.status === 'GESENDET' ? 'Später erinnern' : 'Neu ansetzen'}</span>
          <select
            value=""
            onChange={(ev) => {
              const minuten = Number(ev.target.value)
              if (minuten) aktion(() => api.erinnerungSpaeter(e.id, minuten))
            }}
          >
            <option value="">…</option>
            {SPAETER.map((s) => (
              <option key={s.minuten} value={s.minuten}>
                {s.text}
              </option>
            ))}
          </select>
        </label>
        <a className="link" href={api.erinnerungKalenderUrl(e.id)} download title="Als Termin mit Alarm in den Kalender des Geräts">
          Kalender
        </a>
        <button className="link gefahr" onClick={() => confirm('Diese Erinnerung löschen?') && aktion(() => api.erinnerungLoeschen(e.id))}>
          Löschen
        </button>
      </footer>
    </article>
  )

  return (
    <>
      {fehler && <p className="fehler">{fehler}</p>}
      {offen.length > 0 && <PushHinweis />}
      {faellig.length > 0 && (
        <section>
          <h2 className="vorschlaege">{faellig.length === 1 ? 'Eine Erinnerung ist fällig' : `${faellig.length} Erinnerungen sind fällig`}</h2>
          {faellig.map(karte)}
        </section>
      )}
      {offen.length > 0 && (
        <section>
          <h2>Erinnerungen</h2>
          {offen.map(karte)}
        </section>
      )}
    </>
  )
}

/** Abgehakte Erinnerung unter „Vergangene“; nur ansehen oder löschen */
export function ErledigteErinnerung({ erinnerung: e, onGeaendert }: { erinnerung: Erinnerung; onGeaendert: () => void }) {
  const [fehler, setFehler] = useState<string | null>(null)
  const loeschen = async () => {
    if (!confirm('Diese Erinnerung löschen?')) return
    try {
      await api.erinnerungLoeschen(e.id)
      onGeaendert()
    } catch (ex) {
      setFehler((ex as Error).message)
    }
  }
  return (
    <article className="karte erinnerung erledigt">
      <p>
        <strong>⏰ {e.text}</strong> <span className="chip">erledigt</span>
      </p>
      <p className="leise klein">
        {erinnerungsZeit(e)}
        {e.notizId && (
          <>
            {' · '}
            <a href={link.notiz(e.notizId)}>aus deiner Notiz</a>
          </>
        )}
      </p>
      {fehler && <p className="fehler">{fehler}</p>}
      <footer>
        <button className="link gefahr" onClick={loeschen}>
          Löschen
        </button>
      </footer>
    </article>
  )
}
